import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPublicSiteContent, upsertPublicSitePageContent, uploadPublicSiteAsset } from '../supabaseClient';

const FEATURE_LABELS = [
  'EFTPOS & Card Payments',
  'Online Ordering & Kiosk',
  'No-Code Website Builder',
  'Products, Modifiers & Staff',
];

const DEFAULT_LANDING = {
  hero: {
    badge: 'All-in-one venue management',
    title: 'Run your whole venue from one place',
    subtitle: 'EFTPOS payments, online ordering, and a no-code website — all in one dashboard. No technical skills needed.',
    primaryBtn: 'Get Started Free',
    secondaryBtn: 'See Pricing',
    stats: [
      { value: '1%', label: 'EFTPOS commission' },
      { value: '$0', label: 'Setup fee' },
      { value: 'Daily', label: 'Settlement' },
    ],
  },
  featuresSection: {
    eyebrow: 'Features',
    title: "Everything you need, nothing you don't",
  },
  features: [
    { title: 'EFTPOS & Card Payments', desc: 'Accept tap, swipe, and chip payments in-store. Daily settlement straight to your account. Simple 1% commission — no terminal hire fees.' },
    { title: 'Online Ordering & Kiosk', desc: 'Customers order from their phone or a tablet kiosk. Orders go straight to your kitchen — no third-party fees, no commission per order.' },
    { title: 'No-Code Website Builder', desc: 'Build a branded storefront with drag-and-drop blocks. Publish changes live in seconds — no developer or hosting costs required.' },
    { title: 'Products, Modifiers & Staff', desc: 'Full menu management with modifier groups. Set opening hours and toggle kiosk or online ordering on or off at any time.' },
  ],
  pricing: {
    eyebrow: 'Pricing',
    title: 'Simple, transparent pricing',
    subtitle: 'No setup fees. No lock-in contracts. Cancel any time.',
    monthly: { price: '59', billing: 'Billed monthly — cancel any time' },
    annual: { price: '49', billing: 'Billed $588/year' },
  },
  planFeatures: [
    'EFTPOS & card payments',
    'Online ordering & kiosk',
    'No-code website builder',
    'Products & modifiers',
    'Staff & hours controls',
    'Daily EFTPOS settlement',
    'Unlimited orders',
  ],
  planNote: '+ 1% on in-store EFTPOS transactions',
  contact: {
    eyebrow: 'Contact',
    title: 'Get in touch',
    subtitle: "Have a question? We'd love to hear from you.",
    email: 'hello@servsolo.com',
    note: 'We typically respond within one business day.',
  },
  stripe: {
    monthlyPriceId: '',
    annualPriceId: '',
  },
};

const deepMerge = (base, override) => {
  if (!override) return base;
  const result = { ...base };
  Object.keys(override).forEach((k) => {
    if (override[k] && typeof override[k] === 'object' && !Array.isArray(override[k])) {
      result[k] = deepMerge(base[k] || {}, override[k]);
    } else if (override[k] !== undefined) {
      result[k] = override[k];
    }
  });
  return result;
};

const Field = ({ label, hint, value, onChange, multiline = false, placeholder = '' }) => (
  <div className="lpe-field">
    <label className="lpe-label">{label}</label>
    {hint && <p className="lpe-hint">{hint}</p>}
    {multiline
      ? <textarea className="lpe-input lpe-textarea" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} />
      : <input className="lpe-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    }
  </div>
);

const Section = ({ title, children }) => (
  <div className="lpe-section">
    <h2 className="lpe-section-title">{title}</h2>
    <div className="lpe-section-body">{children}</div>
  </div>
);

const LandingPageEditor = () => {
  const [content, setContent] = useState(DEFAULT_LANDING);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const logoInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchPublicSiteContent();
      const brandingRow = rows.find(r => r.page_slug === 'branding');
      const landingRow = rows.find(r => r.page_slug === 'landing');
      if (brandingRow?.content) setBranding(brandingRow.content);
      if (landingRow?.content) setContent(deepMerge(DEFAULT_LANDING, landingRow.content));
    } catch (err) {
      setError('Failed to load content: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBranding = async (patch) => {
    const next = { ...(branding || {}), ...patch };
    setBranding(next);
    await upsertPublicSitePageContent('branding', next);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setError('');
    try {
      const result = await uploadPublicSiteAsset(file, 'branding');
      if (!result?.url) throw new Error('Upload failed — no URL returned');
      await saveBranding({ logoImageUrl: result.url, logoAlt: 'Servsolo logo' });
      setNotice('Logo updated.');
      setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setError('Logo upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Remove the current logo? The site will show text fallback instead.')) return;
    setLogoUploading(true);
    try {
      await saveBranding({ logoImageUrl: '', logoAlt: '' });
      setNotice('Logo removed.');
      setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setError('Failed to remove logo: ' + (err.message || 'Unknown error'));
    } finally {
      setLogoUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await upsertPublicSitePageContent('landing', content);
      setNotice('Saved successfully.');
      setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setError('Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const set = (path, value) => {
    setContent(prev => {
      const keys = path.split('.');
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setFeature = (i, key, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.features[i][key] = value;
      return next;
    });
  };

  const setPlanFeature = (i, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.planFeatures[i] = value;
      return next;
    });
  };

  const addPlanFeature = () => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.planFeatures = [...(next.planFeatures || []), ''];
      return next;
    });
  };

  const removePlanFeature = (i) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.planFeatures = next.planFeatures.filter((_, idx) => idx !== i);
      return next;
    });
  };

  if (loading) return <div className="lpe-state">Loading landing page content...</div>;

  return (
    <div className="lpe-wrap">
      <div className="lpe-header">
        <div>
          <h1 className="lpe-title">Landing Page Editor</h1>
          <p className="lpe-subtitle">Edit the text and pricing shown on <strong>servsolo.com</strong>. Changes are live immediately after saving.</p>
        </div>
        <div className="lpe-header-actions">
          <a href="/" target="_blank" rel="noopener noreferrer" className="lpe-preview-btn">Preview site ↗</a>
          <button className="lpe-save-btn" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && <div className="lpe-notice lpe-notice-error">{error}</div>}
      {notice && <div className="lpe-notice lpe-notice-success">{notice}</div>}

      {/* LOGO */}
      <Section title="Logo">
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleLogoUpload}
        />
        <div className="lpe-logo-row">
          <button
            className="lpe-logo-clickable"
            onClick={() => logoInputRef.current?.click()}
            disabled={logoUploading}
            title="Click to replace logo"
          >
            {logoUploading
              ? <div className="lpe-logo-uploading">Uploading...</div>
              : branding?.logoImageUrl
                ? <img src={branding.logoImageUrl} alt="Current logo" className="lpe-logo-preview" />
                : <div className="lpe-logo-empty">Click to upload logo</div>
            }
            <div className="lpe-logo-overlay">Replace</div>
          </button>
          {branding?.logoImageUrl && (
            <button
              className="lpe-logo-btn lpe-logo-btn-danger"
              onClick={handleLogoDelete}
              disabled={logoUploading}
            >
              Remove
            </button>
          )}
        </div>
        <p className="lpe-hint" style={{ marginTop: 8 }}>Click the logo to replace it. PNG or SVG with transparent background recommended. Max 15 MB.</p>
      </Section>

      {/* HERO */}
      <Section title="Hero Section">
        <Field label="Badge text" value={content.hero.badge} onChange={v => set('hero.badge', v)} placeholder="All-in-one venue management" />
        <Field label="Main headline" value={content.hero.title} onChange={v => set('hero.title', v)} placeholder="Run your whole venue from one place" />
        <Field label="Subtitle" multiline value={content.hero.subtitle} onChange={v => set('hero.subtitle', v)} placeholder="EFTPOS payments, online ordering..." />
        <div className="lpe-two-col">
          <Field label="Primary button text" value={content.hero.primaryBtn || ''} onChange={v => set('hero.primaryBtn', v)} placeholder="Get Started Free" />
          <Field label="Secondary button text" value={content.hero.secondaryBtn || ''} onChange={v => set('hero.secondaryBtn', v)} placeholder="See Pricing" />
        </div>
        <p className="lpe-label" style={{ marginTop: 16 }}>Stats row (3 items)</p>
        {(content.hero.stats || []).map((s, i) => (
          <div key={i} className="lpe-two-col" style={{ marginTop: 8 }}>
            <Field label={`Stat ${i + 1} value`} value={s.value} onChange={v => {
              setContent(prev => { const n = JSON.parse(JSON.stringify(prev)); n.hero.stats[i].value = v; return n; });
            }} placeholder="1%" />
            <Field label={`Stat ${i + 1} label`} value={s.label} onChange={v => {
              setContent(prev => { const n = JSON.parse(JSON.stringify(prev)); n.hero.stats[i].label = v; return n; });
            }} placeholder="EFTPOS commission" />
          </div>
        ))}
      </Section>

      {/* FEATURES */}
      <Section title="Features Section">
        <div className="lpe-two-col">
          <Field label="Eyebrow label" value={content.featuresSection?.eyebrow || ''} onChange={v => set('featuresSection.eyebrow', v)} placeholder="Features" />
          <Field label="Section headline" value={content.featuresSection?.title || ''} onChange={v => set('featuresSection.title', v)} placeholder="Everything you need, nothing you don't" />
        </div>
        <p className="lpe-hint" style={{ marginTop: 12 }}>4 fixed feature cards:</p>
        {content.features.map((f, i) => (
          <div key={i} className="lpe-feature-block">
            <div className="lpe-feature-num">{i + 1}</div>
            <div className="lpe-feature-fields">
              <Field label="Title" value={f.title} onChange={v => setFeature(i, 'title', v)} placeholder={FEATURE_LABELS[i]} />
              <Field label="Description" multiline value={f.desc} onChange={v => setFeature(i, 'desc', v)} />
            </div>
          </div>
        ))}
      </Section>

      {/* PRICING */}
      <Section title="Pricing Section">
        <div className="lpe-two-col">
          <Field label="Eyebrow label" value={content.pricing.eyebrow || ''} onChange={v => set('pricing.eyebrow', v)} placeholder="Pricing" />
          <Field label="Section headline" value={content.pricing.title || ''} onChange={v => set('pricing.title', v)} placeholder="Simple, transparent pricing" />
        </div>
        <Field label="Subtitle" value={content.pricing.subtitle || ''} onChange={v => set('pricing.subtitle', v)} placeholder="No setup fees. No lock-in contracts. Cancel any time." />
        <div className="lpe-two-col" style={{ marginTop: 16 }}>
          <div className="lpe-plan-block">
            <h3 className="lpe-plan-label">Monthly Plan</h3>
            <Field label="Price (number only, e.g. 99)" value={content.pricing.monthly.price} onChange={v => set('pricing.monthly.price', v)} placeholder="99" />
            <Field label="Billing note" value={content.pricing.monthly.billing} onChange={v => set('pricing.monthly.billing', v)} placeholder="Billed monthly — cancel any time" />
          </div>
          <div className="lpe-plan-block">
            <h3 className="lpe-plan-label">Annual Plan</h3>
            <Field label="Price per month (number only, e.g. 79)" value={content.pricing.annual.price} onChange={v => set('pricing.annual.price', v)} placeholder="79" />
            {(() => {
              const p = parseFloat(content.pricing.annual.price);
              const m = parseFloat(content.pricing.monthly.price);
              const total = p > 0 ? Math.round(p * 12) : null;
              const pct = m > 0 && p > 0 && p < m ? Math.round((m - p) / m * 100) : null;
              return (
                <p className="lpe-hint" style={{ marginTop: 4 }}>
                  {total ? <>Displays as: <strong>Billed ${total}/year</strong></> : 'Enter a price to see calculated total.'}
                  {pct ? <> · Badge: <strong>Save {pct}%</strong></> : null}
                </p>
              );
            })()}
          </div>
        </div>
      </Section>

      {/* PLAN FEATURES */}
      <Section title="Plan Feature List (both pricing cards)">
        <p className="lpe-hint">These checkmark items appear on both the monthly and annual pricing cards.</p>
        {(content.planFeatures || []).map((f, i) => (
          <div key={i} className="lpe-plan-feature-row">
            <input
              className="lpe-input"
              value={f}
              onChange={e => setPlanFeature(i, e.target.value)}
              placeholder={`Feature ${i + 1}`}
            />
            <button
              className="lpe-logo-btn lpe-logo-btn-danger"
              onClick={() => removePlanFeature(i)}
              style={{ flexShrink: 0 }}
            >
              Remove
            </button>
          </div>
        ))}
        <button className="lpe-add-btn" onClick={addPlanFeature}>+ Add item</button>
        <Field
          label="Note below feature list (e.g. transaction fee)"
          value={content.planNote || ''}
          onChange={v => set('planNote', v)}
          placeholder="+ 1% on in-store EFTPOS transactions"
        />
      </Section>

      {/* CONTACT */}
      <Section title="Contact Section">
        <div className="lpe-two-col">
          <Field label="Eyebrow label" value={content.contact.eyebrow || ''} onChange={v => set('contact.eyebrow', v)} placeholder="Contact" />
          <Field label="Section headline" value={content.contact.title || ''} onChange={v => set('contact.title', v)} placeholder="Get in touch" />
        </div>
        <Field label="Subtitle" value={content.contact.subtitle || ''} onChange={v => set('contact.subtitle', v)} placeholder="Have a question? We'd love to hear from you." />
        <Field label="Email address" value={content.contact.email} onChange={v => set('contact.email', v)} placeholder="hello@servsolo.com" />
        <Field label="Response note (shown below email)" value={content.contact.note} onChange={v => set('contact.note', v)} placeholder="We typically respond within one business day." />
      </Section>

      {/* STRIPE */}
      <Section title="Stripe Price IDs">
        <p className="lpe-hint">
          Enter your Stripe Price IDs from the <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="lpe-link">Stripe Dashboard → Products</a>. These are used when customers click "Get Started" on the pricing cards.
        </p>
        <div className="lpe-two-col">
          <Field
            label="Monthly Stripe Price ID"
            value={content.stripe.monthlyPriceId}
            onChange={v => set('stripe.monthlyPriceId', v)}
            placeholder="price_xxxxxxxxxxxxxxxxxx"
          />
          <Field
            label="Annual Stripe Price ID"
            value={content.stripe.annualPriceId}
            onChange={v => set('stripe.annualPriceId', v)}
            placeholder="price_xxxxxxxxxxxxxxxxxx"
          />
        </div>
        {(!content.stripe.monthlyPriceId || !content.stripe.annualPriceId) && (
          <div className="lpe-stripe-warning">
            Stripe Price IDs are not set — "Get Started" buttons will redirect to <code>/login</code> but checkout won't complete. Set the IDs above to enable full Stripe checkout.
          </div>
        )}
      </Section>

      <div className="lpe-save-footer">
        <button className="lpe-save-btn" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default LandingPageEditor;
